<template>
  <NuxtPage />
  <NetworkErrorBanner />
</template>
<script setup lang="ts">
import { setupPipeState, PipeDisplayMode } from '@/render/composables/usePipe' // adjust path if needed
import NetworkErrorBanner from '@/render/NetworkErrorBanner.vue'
import type { PipeFullReleaseDto } from '@api/types.gen'
import database from '../database.json'
import activeExtensions from 'virtual:extension-registry'
import { hideLoadingOverlay } from '../lib/loadingOverlay'

const pipe = setupPipeState()
pipe.setMode(PipeDisplayMode.STATIC)

// Inject build-time constants BEFORE initialize() so extensions that read
// them in their setup() callbacks (e.g. Klaviyo reading system.pipeConfigs)
// see the values. initialize() calls registerExtensions() → setup() synchronously.
const config = useRuntimeConfig()
pipe.constants.value = {
  ...pipe.constants.value,
  // Bake the build-time environment (set by codebuild via APP_ENV) so any
  // component or integration can read it via `pipe.getData('const.system.appEnv')`.
  // Used by the pipeorders integration to gate the dev-only NPS webhook replay
  // call so it never fires from staging / production builds. See builder/nuxt.config.ts.
  'system.appEnv': config.public.appEnv,
  // Pipe configs resolved for this release's context at build time (see
  // builder/nuxt.config.ts + builder/lib/pipeConfigs.ts). Seeded UNFILTERED —
  // the list can carry any config type (KLAVIYO today; Meta / Google / etc.
  // later); consumers pick what they need by `type`. Render code reads it via
  // `pipe.getData('const.system.pipeConfigs')` (typed PipeBuildConfigList).
  // In the editor preview the constant is absent — default to
  // `{ configs: [] }` when consuming.
  'system.pipeConfigs': config.public.pipeConfigs,
}

pipe.initialize({ initialData: database as PipeFullReleaseDto, extensions: activeExtensions })

// The loading overlay (BUILD_SETTINGS `loadingOverlay`, builder/lib/loadingOverlay.ts) stays
// until the page is ready to use, which is later than hydration: the visitor's saved answers
// are merged in a tick after it and re-render the page, and a blocking integration (an order
// restore) can take longer still. whenReady() waits for both, the tick for that render to
// reach the DOM. A blocking integration that failed has finished too: the page gets no readier.
onMounted(async () => {
  await pipe.whenReady().catch(() => {})
  await nextTick()
  hideLoadingOverlay()
})
</script>
